/*
 * Shared phased-progress list for Deckorate's AI image feature panels
 * (Sketch to Slide, Photo Headshot, ...) — a vertical checklist that marks
 * each phase done/active/waiting as a run progresses. Presentation-agnostic:
 * callers pass their own CSS module's class names so each panel's existing
 * look is unchanged.
 */

export type StepState = "done" | "active" | "waiting";

/** A CSS module's default export (Vite's `CSSModuleClasses`) — an index
 *  signature, not named properties, so callers can pass their `styles`
 *  import directly. */
export type ProgressStepsClassNames = Readonly<Record<string, string | undefined>>;

export function stepStateAt(index: number, currentIndex: number): StepState {
  if (index < currentIndex) return "done";
  return index === currentIndex ? "active" : "waiting";
}

function StepMark({ state, spinnerClassName }: Readonly<{ state: StepState; spinnerClassName: string }>) {
  if (state === "done") return <>✓</>;
  if (state === "active") return <span className={spinnerClassName} />;
  return <>○</>;
}

export interface ProgressStepsProps<TPhase extends string> {
  readonly phases: ReadonlyArray<{ readonly phase: TPhase; readonly label: string }>;
  readonly current: TPhase;
  readonly ariaLabel: string;
  readonly classNames: ProgressStepsClassNames;
}

export function ProgressSteps<TPhase extends string>({
  phases,
  current,
  ariaLabel,
  classNames,
}: Readonly<ProgressStepsProps<TPhase>>) {
  const currentIndex = phases.findIndex((step) => step.phase === current);
  const stepClass: Record<StepState, string> = {
    done: classNames.stepDone!,
    active: classNames.stepActive!,
    waiting: classNames.stepWaiting!,
  };

  return (
    <ol className={classNames.steps} aria-label={ariaLabel}>
      {phases.map((step, index) => {
        const state = stepStateAt(index, currentIndex);
        return (
          <li
            key={step.phase}
            className={`${classNames.step} ${stepClass[state]}`}
            aria-current={state === "active" || undefined}
          >
            <span className={classNames.stepMark} aria-hidden="true">
              <StepMark state={state} spinnerClassName={classNames.spinner!} />
            </span>
            <span>{step.label}</span>
          </li>
        );
      })}
    </ol>
  );
}
